// src/components/TelemedClientSessions.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = 'https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod';

/** Utilitaires d'affichage **/
function toMtlPartsFromUtc(utcString) {
  // Transforme un ISO UTC (ou "YYYY-MM-DD HH:mm:ss" UTC) en { date, time } MTL
  if (!utcString) return { date: '—', time: '—' };
  try {
    const s = utcString.includes('T') ? utcString : `${utcString.replace(' ', 'T')}Z`;
    const d = new Date(s);
    const date = new Intl.DateTimeFormat('fr-CA', { timeZone: 'America/Toronto' }).format(d);
    const time = new Intl.DateTimeFormat('fr-CA', {
      timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit', hour12: false
    }).format(d);
    return { date, time };
  } catch {
    return { date: utcString, time: '' };
  }
}
function splitMtlLocalString(mtlStr) {
  // mtlStr = "YYYY-MM-DD HH:mm:ss.xxx" => { date, time }
  if (!mtlStr || typeof mtlStr !== 'string') return { date: '—', time: '—' };
  const [date, time] = mtlStr.split(' ');
  return { date: date || '—', time: (time || '').substring(0, 5) || '—' };
}
function durationTextFromUtc(startUtc, endUtc) {
  // startUtc/endUtc en ISO UTC (ou "YYYY-MM-DD HH:mm:ss" UTC)
  if (!startUtc || !endUtc) return '—';
  try {
    const s = new Date((startUtc.includes('T') ? startUtc : startUtc.replace(' ', 'T') + 'Z')).getTime();
    const e = new Date((endUtc.includes('T') ? endUtc : endUtc.replace(' ', 'T') + 'Z')).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return '—';
    const mins = Math.round((e - s) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m} min`;
  } catch {
    return '—';
  }
}
function Pill({ children, color = '#1976d2', bg = '#eef3ff' }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      background: bg, color, fontSize: 12, fontWeight: 600
    }}>
      {children}
    </span>
  );
}
function pillForApptStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'confirmed') return <Pill color="#1b5e20" bg="#e8f5e9">confirmé</Pill>;
  if (s === 'scheduled') return <Pill color="#1565c0" bg="#e3f2fd">prévu</Pill>;
  if (s === 'cancelled') return <Pill color="#c62828" bg="#fdecea">annulé</Pill>;
  return <Pill>—</Pill>;
}
function pillForTransStatus(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'COMPLETED') return <Pill color="#1b5e20" bg="#e8f5e9">COMPLETED</Pill>;
  if (s === 'IN_PROGRESS') return <Pill color="#1565c0" bg="#e3f2fd">IN_PROGRESS</Pill>;
  if (s === 'FAILED') return <Pill color="#c62828" bg="#fdecea">FAILED</Pill>;
  return <Pill>—</Pill>;
}

/** Table de sessions télémed **/
export default function TelemedClientSessions() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const professionalEmail = useMemo(() => localStorage.getItem('user_email'), []);
  const [sessions, setSessions]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [open, setOpen]           = useState(() => new Set()); // sessions ouvertes (détails transcriptions)

  const toggleOpen = (sid) => {
    setOpen(prev => {
      const cp = new Set(prev);
      (cp.has(sid) ? cp.delete(sid) : cp.add(sid));
      return cp;
    });
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1) Résoudre professional_id depuis l'email
      const me = await fetch(`${API_URL}/users/email/${encodeURIComponent(professionalEmail || '')}`);
      if (!me.ok) throw new Error('users/email non disponible');
      const meJson = await me.json();
      const proId = meJson.user_id;

      // 2) Charger toutes les sessions pour ce (pro, patient)
      //    NB : backend renvoie pour chaque session :
      //    - start_time_utc, end_time_utc (ISO)
      //    - start_mtl, end_mtl (texte "YYYY-MM-DD HH:mm:ss.sss" local Toronto)
      //    - appt_status, service_name, mode
      //    - transcriptions: [{id,status,job_mode,language_code,created_at,completed_at,json_presigned_url,audio_presigned_url}]
      const res = await fetch(`${API_URL}/professionals/${proId}/clients/${patientId}/sessions`);
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'sessions endpoint a échoué');
      }
      const data = await res.json();
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erreur chargement sessions :', e);
      setError(e.message || 'Erreur réseau');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (professionalEmail) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalEmail, patientId]);

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ marginBottom: 6 }}>Sessions de téléconsultation</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        Patient : <code>{patientId}</code>
      </p>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <button onClick={() => navigate('/professional/clients')} style={btnSecondary}>
          ⬅️ Retour à mes clients
        </button>
        <button onClick={load} style={btnGhost}>↻ Rafraîchir</button>
      </div>

      {loading && <p>Chargement…</p>}
      {error && (
        <div style={{ background: '#fdecea', color: '#c62828', padding: 12, borderRadius: 6, marginBottom: 8 }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        sessions.length === 0 ? (
          <div style={emptyBox}>
            Aucune session de téléconsultation trouvée pour ce client.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
              <thead>
                <tr style={{ background: '#f6f6f6' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Heure</th>
                  <th style={thStyle}>Durée</th>
                  <th style={thStyle}>Service</th>
                  <th style={thStyle}>Mode</th>
                  <th style={thStyle}>Statut RDV</th>
                  <th style={thStyle}>Transcription (dernière)</th>
                  <th style={thStyle}>Résumé</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  // Préférence : on affiche start_mtl/end_mtl s'ils existent (du backend), sinon conversion UTC → MTL côté front
                  const mtlStart = s.start_mtl ? splitMtlLocalString(s.start_mtl) : toMtlPartsFromUtc(s.start_time_utc);
                  const mtlEnd   = s.end_mtl   ? splitMtlLocalString(s.end_mtl)   : toMtlPartsFromUtc(s.end_time_utc);
                  const dura     = durationTextFromUtc(s.start_time_utc, s.end_time_utc);

                  const list = Array.isArray(s.transcriptions) ? s.transcriptions : [];
                  const latest = list.length > 0 ? list[0] : null; // backend normalement renvoie trié DESC created_at

                  return (
                    <React.Fragment key={s.session_id}>
                      <tr>
                        <td style={tdStyle}>{mtlStart.date}</td>
                        <td style={tdStyle}>{mtlStart.time}</td>
                        <td style={tdStyle}>{dura}</td>
                        <td style={tdStyle}>{s.service_name || '—'}</td>
                        <td style={tdStyle}>{s.mode || '—'}</td>
                        <td style={tdStyle}>{pillForApptStatus(s.appt_status)}</td>

                        <td style={tdStyle}>
                          {latest ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              {pillForTransStatus(latest.status)}
                              <span style={{ color: '#666', fontSize: 12 }}>
                                {latest.job_mode || '—'} / {latest.language_code || '—'}
                              </span>
                              {latest.json_presigned_url ? (
                                <a href={latest.json_presigned_url} target="_blank" rel="noreferrer" style={linkBtn}>📄 JSON</a>
                              ) : <span style={{ color: '#999' }}>JSON —</span>}
                              {latest.audio_presigned_url ? (
                                <a href={latest.audio_presigned_url} target="_blank" rel="noreferrer" style={{ ...linkBtn, background: '#e8f5e9', color: '#2e7d32' }}>🎧 Audio</a>
                              ) : <span style={{ color: '#999' }}>Audio —</span>}
                              <span style={{ color: '#999', fontSize: 12 }}>
                                {latest.created_at ? `(${latest.created_at})` : ''}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>—</span>
                          )}
                        </td>

                        <td style={tdStyle}>
                          <a
                            href={`${API_URL}/telemedicine-sessions/${s.session_id}/transcript/summary`}
                            target="_blank" rel="noreferrer" style={linkBtn}
                          >
                            📝 Résumé
                          </a>
                          <a
                            href={`${API_URL}/telemedicine-sessions/${s.session_id}/transcript/narrative?locale=fr`}
                            target="_blank" rel="noreferrer" style={{ ...linkBtn, background: '#ede7f6', color: '#4527a0', marginLeft: 8 }}
                          >
                            📚 Narratif
                          </a>
                        </td>

                        <td style={tdStyle}>
                          {list.length > 1 && (
                            <button onClick={() => toggleOpen(s.session_id)} style={btnGhost}>
                              {open.has(s.session_id) ? '▲ Détails' : `▼ Détails (${list.length})`}
                            </button>
                          )}
                        </td>
                      </tr>

                      {open.has(s.session_id) && list.length > 0 && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0, background: '#fafafa' }}>
                            <div style={{ padding: 12 }}>
                              <h4 style={{ marginTop: 0 }}>Toutes les transcriptions</h4>
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                                  <thead>
                                    <tr style={{ background: '#f9f9f9' }}>
                                      <th style={thStyleSmall}>Statut</th>
                                      <th style={thStyleSmall}>Mode</th>
                                      <th style={thStyleSmall}>Langue</th>
                                      <th style={thStyleSmall}>Créée</th>
                                      <th style={thStyleSmall}>Terminée</th>
                                      <th style={thStyleSmall}>JSON</th>
                                      <th style={thStyleSmall}>Audio</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {list.map((t) => (
                                      <tr key={t.id}>
                                        <td style={tdStyleSmall}>{pillForTransStatus(t.status)}</td>
                                        <td style={tdStyleSmall}>{t.job_mode || '—'}</td>
                                        <td style={tdStyleSmall}>{t.language_code || '—'}</td>
                                        <td style={tdStyleSmall}>{t.created_at || '—'}</td>
                                        <td style={tdStyleSmall}>{t.completed_at || '—'}</td>
                                        <td style={tdStyleSmall}>
                                          {t.json_presigned_url ? (
                                            <a href={t.json_presigned_url} target="_blank" rel="noreferrer" style={linkBtn}>📄 Ouvrir</a>
                                          ) : <span style={{ color: '#999' }}>—</span>}
                                        </td>
                                        <td style={tdStyleSmall}>
                                          {t.audio_presigned_url ? (
                                            <a href={t.audio_presigned_url} target="_blank" rel="noreferrer" style={{ ...linkBtn, background: '#e8f5e9', color: '#2e7d32' }}>🎧 Écouter</a>
                                          ) : <span style={{ color: '#999' }}>—</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

/** Styles **/
const thStyle       = { border: '1px solid #eee', padding: 10, textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' };
const tdStyle       = { border: '1px solid #f3f3f3', padding: 10, verticalAlign: 'top' };
const thStyleSmall  = { border: '1px solid #eee', padding: 8, textAlign: 'left', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' };
const tdStyleSmall  = { border: '1px solid #f3f3f3', padding: 8, fontSize: 13, verticalAlign: 'top' };
const btnSecondary  = { background: '#888', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' };
const btnGhost      = { background: '#6c43f31a', color: '#4527a0', border: '1px solid #6c43f333', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' };
const linkBtn       = { background: '#eef3ff', color: '#1976d2', padding: '6px 10px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' };
const emptyBox      = { background: '#fafafa', border: '1px dashed #ddd', padding: 18, borderRadius: 8, color: '#777', marginTop: 10 };